import React, { useState, useEffect } from 'react';
import { ConfigDocument, S3BucketConfig, TestResult } from '../../types';
import { testS3Connection } from '../../services/configApi';

interface S3BucketsTabProps {
  config: ConfigDocument | null;
  onSave: (config: ConfigDocument) => Promise<void>;
  loading: boolean;
}

interface ValidationErrors {
  sourceBucket?: string;
  sourceAccount?: string;
  archiveBucket?: string;
  archiveAccount?: string;
}

interface ConnectionTestState {
  loading: boolean;
  result: TestResult | null;
}

const EMPTY_S3: S3BucketConfig = { bucket: '', account: '', region: 'us-east-1' };

const S3BucketsTab: React.FC<S3BucketsTabProps> = ({ config, onSave, loading }) => {
  const [sourceS3, setSourceS3] = useState<S3BucketConfig>({ ...EMPTY_S3 });
  const [archiveS3, setArchiveS3] = useState<S3BucketConfig>({ ...EMPTY_S3 });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [sourceTest, setSourceTest] = useState<ConnectionTestState>({ loading: false, result: null });
  const [archiveTest, setArchiveTest] = useState<ConnectionTestState>({ loading: false, result: null });

  // Sync local state when config prop changes
  useEffect(() => {
    if (config) {
      setSourceS3({
        bucket: config.sourceS3?.bucket || '',
        account: config.sourceS3?.account || '',
        region: config.sourceS3?.region || 'us-east-1',
        roleArn: config.sourceS3?.roleArn || '',
      });
      setArchiveS3({
        bucket: config.archiveS3?.bucket || '',
        account: config.archiveS3?.account || '',
        region: config.archiveS3?.region || 'us-east-1',
        roleArn: config.archiveS3?.roleArn || '',
      });
    }
  }, [config]);

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!sourceS3.bucket.trim()) {
      newErrors.sourceBucket = 'Bucket name is required';
    }
    if (!/^\d{12}$/.test(sourceS3.account)) {
      newErrors.sourceAccount = 'Account ID must be exactly 12 digits';
    }
    if (!archiveS3.bucket.trim()) {
      newErrors.archiveBucket = 'Bucket name is required';
    }
    if (!/^\d{12}$/.test(archiveS3.account)) {
      newErrors.archiveAccount = 'Account ID must be exactly 12 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !config) return;

    setSaving(true);
    try {
      await onSave({
        ...config,
        sourceS3: {
          bucket: sourceS3.bucket,
          account: sourceS3.account,
          region: sourceS3.region,
          roleArn: sourceS3.roleArn,
        },
        archiveS3: {
          bucket: archiveS3.bucket,
          account: archiveS3.account,
          region: archiveS3.region,
          roleArn: archiveS3.roleArn,
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSource = async () => {
    setSourceTest({ loading: true, result: null });
    try {
      const result = await testS3Connection({
        bucket: sourceS3.bucket,
        roleArn: sourceS3.roleArn || '',
        region: sourceS3.region,
      });
      setSourceTest({ loading: false, result });
    } catch (err: any) {
      setSourceTest({
        loading: false,
        result: { success: false, message: err.message || 'Connection test failed' },
      });
    }
  };

  const handleTestArchive = async () => {
    setArchiveTest({ loading: true, result: null });
    try {
      const result = await testS3Connection({
        bucket: archiveS3.bucket,
        roleArn: archiveS3.roleArn || '',
        region: archiveS3.region,
      });
      setArchiveTest({ loading: false, result });
    } catch (err: any) {
      setArchiveTest({
        loading: false,
        result: { success: false, message: err.message || 'Connection test failed' },
      });
    }
  };

  if (loading && !config) {
    return <div className="text-gray-500">Loading configuration...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Source S3 Bucket */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Source S3 Bucket</h3>
          <button
            type="button"
            onClick={handleTestSource}
            disabled={sourceTest.loading || !sourceS3.bucket}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sourceTest.loading ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {sourceTest.result && (
          <TestResultBanner result={sourceTest.result} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="source-bucket" className="block text-sm font-medium text-gray-700">
              Bucket Name
            </label>
            <input
              id="source-bucket"
              type="text"
              value={sourceS3.bucket}
              onChange={(e) => setSourceS3({ ...sourceS3, bucket: e.target.value })}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors.sourceBucket ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="my-source-bucket"
            />
            {errors.sourceBucket && (
              <p className="mt-1 text-sm text-red-600">{errors.sourceBucket}</p>
            )}
          </div>

          <div>
            <label htmlFor="source-account" className="block text-sm font-medium text-gray-700">
              AWS Account ID
            </label>
            <input
              id="source-account"
              type="text"
              value={sourceS3.account}
              onChange={(e) => setSourceS3({ ...sourceS3, account: e.target.value })}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors.sourceAccount ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="123456789012"
              maxLength={12}
            />
            {errors.sourceAccount && (
              <p className="mt-1 text-sm text-red-600">{errors.sourceAccount}</p>
            )}
          </div>

          <div>
            <label htmlFor="source-region" className="block text-sm font-medium text-gray-700">
              Region
            </label>
            <input
              id="source-region"
              type="text"
              value={sourceS3.region}
              onChange={(e) => setSourceS3({ ...sourceS3, region: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="us-east-1"
            />
          </div>

          <div>
            <label htmlFor="source-role-arn" className="block text-sm font-medium text-gray-700">
              Role ARN
            </label>
            <input
              id="source-role-arn"
              type="text"
              value={sourceS3.roleArn || ''}
              onChange={(e) => setSourceS3({ ...sourceS3, roleArn: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="arn:aws:iam::123456789012:role/MyRole"
            />
          </div>
        </div>
      </div>

      {/* Archive S3 Bucket */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Archive S3 Bucket</h3>
          <button
            type="button"
            onClick={handleTestArchive}
            disabled={archiveTest.loading || !archiveS3.bucket}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {archiveTest.loading ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {archiveTest.result && (
          <TestResultBanner result={archiveTest.result} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="archive-bucket" className="block text-sm font-medium text-gray-700">
              Bucket Name
            </label>
            <input
              id="archive-bucket"
              type="text"
              value={archiveS3.bucket}
              onChange={(e) => setArchiveS3({ ...archiveS3, bucket: e.target.value })}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors.archiveBucket ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="my-archive-bucket"
            />
            {errors.archiveBucket && (
              <p className="mt-1 text-sm text-red-600">{errors.archiveBucket}</p>
            )}
          </div>

          <div>
            <label htmlFor="archive-account" className="block text-sm font-medium text-gray-700">
              AWS Account ID
            </label>
            <input
              id="archive-account"
              type="text"
              value={archiveS3.account}
              onChange={(e) => setArchiveS3({ ...archiveS3, account: e.target.value })}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                errors.archiveAccount ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="123456789012"
              maxLength={12}
            />
            {errors.archiveAccount && (
              <p className="mt-1 text-sm text-red-600">{errors.archiveAccount}</p>
            )}
          </div>

          <div>
            <label htmlFor="archive-region" className="block text-sm font-medium text-gray-700">
              Region
            </label>
            <input
              id="archive-region"
              type="text"
              value={archiveS3.region}
              onChange={(e) => setArchiveS3({ ...archiveS3, region: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="us-east-1"
            />
          </div>

          <div>
            <label htmlFor="archive-role-arn" className="block text-sm font-medium text-gray-700">
              Role ARN
            </label>
            <input
              id="archive-role-arn"
              type="text"
              value={archiveS3.roleArn || ''}
              onChange={(e) => setArchiveS3({ ...archiveS3, roleArn: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="arn:aws:iam::123456789012:role/MyRole"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading || !config}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

/** Small helper component for test result banners */
const TestResultBanner: React.FC<{ result: TestResult }> = ({ result }) => (
  <div
    className={`mb-4 p-3 rounded-md flex items-center space-x-2 ${
      result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
    }`}
    role="alert"
  >
    <span className="text-lg">{result.success ? '✓' : '✗'}</span>
    <span className="text-sm">{result.message || (result.success ? 'Connection successful' : 'Connection failed')}</span>
  </div>
);

export default S3BucketsTab;
